import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MissesPanel from './MissesPanel';
import ComplianceTasks from './ComplianceTasks';
import CQI from './CQI';
import { T } from './theme';
import { PatientAvatar } from './EMRPhotos';

const API = 'https://tcio-api.onrender.com';
const card = { background:T.surface, border:`1px solid ${T.b200}`, borderRadius:12,
  boxShadow:'0 1px 2px rgba(110,59,71,.05), 0 3px 10px rgba(110,59,71,.06)' };
const inp = { font:'inherit', fontSize:13, padding:'8px 10px', borderRadius:8,
  border:`1px solid ${T.b300}`, color:T.ink, background:'#fff' };
const colHead = { fontSize:10.5, letterSpacing:'0.8px', textTransform:'uppercase', color:T.inkSoft, fontWeight:700 };
const pill = (bg, fg, text) => <span style={{ background:bg, color:fg, borderRadius:99,
  padding:'2px 10px', fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>{text}</span>;
const dot = (color) => <span style={{ width:8, height:8, borderRadius:'50%', background:color, display:'inline-block', flexShrink:0 }} />;

const SEV_META = {
  safety_critical: { chip: () => pill('#fdecea','#b30000','Safety Critical'), rank:3 },
  critical:        { chip: () => pill('#fdecea','#8b0000','Critical'), rank:2 },
  important:       { chip: () => pill('#fff3e0','#b45f06','Important'), rank:1 },
};
const STATE_PILL = {
  overdue:   () => pill('#fdecea','#b30000','OVERDUE'),
  at_risk:   () => pill('#fff8e1','#8d6e00','AT RISK'),
  due:       () => pill(T.warningBg,T.warning,'Due'),
  satisfied: () => pill(T.passBg,T.pass,'Satisfied'),
  exempt:    () => pill('#eef2f7',T.inkSoft,'Exempt'),
};
const statePill = (s) => (STATE_PILL[s] || (() => pill('#eef2f7',T.inkSoft,String(s||'UNKNOWN').toUpperCase())))();
const LIC_PILL = {
  expired: () => pill('#fdecea','#b30000','EXPIRED'),
  red:     () => pill('#fdecea','#b30000','<= 30 days'),
  amber:   () => pill(T.warningBg,T.warning,'<= 60 days'),
  notice:  () => pill(T.infoBg,T.info,'<= 90 days'),
  ok:      () => pill(T.passBg,T.pass,'Current'),
};
const OWNER_LABEL = { admissions:'Admissions', therapist:'Therapist', case_manager:'Case Mgr',
  clinical:'Clinical', bht:'BHT', facility_admin:'Facility Admin' };
const ownerChip = (ro, compact) => ro ? (
  <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
    <span style={{ width:24, height:24, borderRadius:'50%', background:'#e8eef5', color:'#22303E',
      display:'inline-flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:10 }}>
      {(OWNER_LABEL[ro] || ro).slice(0,2).toUpperCase()}</span>
    {!compact && <span style={{ fontSize:12, color:T.inkSoft, fontWeight:600 }}>{OWNER_LABEL[ro] || ro}</span>}
  </span>
) : null;
const CAT_FOR = (c) => /round/i.test(c.name) ? 'Rounds' : /vital/i.test(c.name) ? 'Vitals' : 'Clinical';
const ACTION_FOR = (c) => {
  if (/round/i.test(c.name)) return ['Start Round','bht'];
  if (/treatment plan|tp /i.test(c.name)) return ['Open Plan','tp'];
  if (/admission agreement|orientation/i.test(c.name)) return ['Open Intake','intake'];
  if (/note|documentation|assessment|questionnaire/i.test(c.name)) return ['Open Chart','notes'];
  return ['Open Chart','overview'];
};
const fmtDT = (v) => v ? new Date(v).toLocaleString('en-US',
  { timeZone:'America/Los_Angeles', month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }) : '';
const fmtD = (v) => v ? new Date(String(v).slice(0,10)+'T00:00:00').toLocaleDateString('en-US',
  { month:'short', day:'numeric', year:'numeric' }) : '';
const HOUR = 3600000;
const SUPERVISOR_ROLES = ['ceo','admin','clinical_director','program_manager','operations','facility_admin'];
const POOL_FOR_ROLE = { bht:['bht'], therapist:['therapist','clinical'], case_manager:['case_manager','clinical'],
  medical:['clinical'], medical_admin:['clinical'], admissions:['admissions'], admissions_support:['admissions'] };

function StatCard({ n, text, sub, tone, active, onClick }) {
  const tones = { red:'#b30000', amber:'#8d6e00', ink:T.ink };
  const c = (n > 0 && tones[tone]) || T.ink;
  return (
    <div onClick={onClick} style={{ ...card, padding:'14px 18px', minWidth:150, flex:'1 1 150px',
      cursor:onClick ? 'pointer' : 'default', border: active ? '2px solid #0878E8' : card.border }}>
      <div style={{ fontSize:27, fontWeight:800, color:c, lineHeight:1.1 }}>{n}</div>
      <div style={{ fontSize:13, fontWeight:700, color:T.ink, marginTop:4 }}>{text}</div>
      {sub && <div style={{ fontSize:11.5, color:T.inkSoft, marginTop:1 }}>{sub}</div>}
    </div>
  );
}

export default function EMRCompliance({ onOpenPatient, user }) {
  const [data, setData] = useState(null);
  const [facilityId, setFacilityId] = useState('');
  const [tab, setTab] = useState('patients');
  const [open, setOpen] = useState({});
  const [expandAll, setExpandAll] = useState({});
  const [showOk, setShowOk] = useState(false);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [locF, setLocF] = useState('');
  const [sevF, setSevF] = useState('');
  const [mine, setMine] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [missSum, setMissSum] = useState(null);   // miss ledger counts (compliance_misses) for the tile

  const load = useCallback(async (fid) => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/api/emr/compliance/dashboard`,
        { params: fid ? { facility_id: fid } : {} });
      setData(r.data); setFacilityId(r.data.facility_id);
      try {
        const m = await axios.get(`${API}/api/compliance/misses/summary`, { params: { facilityId: r.data.facility_id } });
        const src = (m.data && m.data.by_source) || [];
        const tot = (k) => src.reduce((a, x) => a + (Number(x[k]) || 0), 0);
        setMissSum({ last_24h: tot('last_24h'), last_7d: tot('last_7d'), unack: tot('unacknowledged_7d') });
      } catch { setMissSum(null); }
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(''); }, [load]);

  if (err) return <div style={{ ...card, padding:16, color:T.blocking, fontWeight:600 }}>{err}</div>;
  if (!data) return <div style={{ color:T.inkSoft, fontSize:13, padding:12 }}>Evaluating compliance...</div>;

  const s = data.summary || {};
  const nowMs = Date.now();
  const isSupervisor = SUPERVISOR_ROLES.includes(user && user.role);
  const myPool = POOL_FOR_ROLE[user && user.role] || null;
  const matchesFilter = (p) => {
    if (filter === 'all') return true;
    if (filter === 'at_risk') return p.checks.some(c => c.state === 'at_risk');
    if (filter === 'safety_critical') return p.checks.some(c => c.severity === 'safety_critical' && ['overdue','due','at_risk'].includes(c.state));
    if (filter === 'due_today') return p.checks.some(c => c.state === 'due' && c.due_at && (new Date(c.due_at).getTime() - nowMs) <= 24 * HOUR);
    if (filter === 'overdue') return p.overdue_count > 0;
    return true;
  };
  const checkVisible = (c) => {
    if (sevF && c.severity !== sevF) return false;
    if (mine && myPool && !myPool.includes(c.role_owner)) return false;
    return true;
  };
  const hasException = (p) => p.overdue_count > 0 || p.checks.some(c => ['due','at_risk'].includes(c.state));
  const shown = (data.patients || []).filter(p =>
    (showOk || hasException(p)) && matchesFilter(p)
    && (!q || (p.patient_name || '').toLowerCase().includes(q.toLowerCase()))
    && (!locF || p.loc === locF)
    && (!(sevF || (mine && myPool)) || p.checks.some(c => checkVisible(c) && c.state !== 'satisfied')));
  const locOptions = [...new Set((data.patients || []).map(p => p.loc).filter(Boolean))];
  const toggleFilter = (f) => setFilter(cur => cur === f ? 'all' : f);
  const highestSev = (p) => {
    let best = null;
    p.checks.forEach(c => {
      if (['overdue','due','at_risk'].includes(c.state) && SEV_META[c.severity] &&
          (!best || SEV_META[c.severity].rank > SEV_META[best].rank)) best = c.severity;
    });
    return best;
  };
  const dueSoonCount = (p) => p.checks.filter(c => ['due','at_risk'].includes(c.state)).length;

  const tabBtn = (key, text) => (
    <button key={key} onClick={() => setTab(key)} style={{ font:'inherit', fontSize:13.5, fontWeight:600,
      padding:'8px 20px', borderRadius:9, cursor:'pointer',
      border: tab === key ? 'none' : `1px solid ${T.b200}`,
      background: tab === key ? T.ink : '#fff', color: tab === key ? '#fff' : T.ink }}>{text}</button>
  );

  return (
    <div>
      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:14, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:8 }}>{tabBtn('patients','Patients')}{tabBtn('staff','Staff')}{tabBtn('facility','Facility')}{tabBtn('tasks','Tasks')}{tabBtn('cqi','CQI')}</div>
        <span style={{ flex:1 }} />
        {!data.locked && (data.facilities || []).length > 0 && (
          <select style={inp} value={facilityId} onChange={e => load(e.target.value)}>
            {(data.facilities || []).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}
        {data.locked && <strong style={{ fontSize:15 }}>{(data.facilities[0] || {}).name}</strong>}
        {loading && <span style={{ fontSize:12.5, color:T.inkSoft }}>Refreshing...</span>}
      </div>

      {tab === 'patients' && (
        <div>
          {(() => {
            const prevent = [];
            (data.patients || []).forEach(p => p.checks.forEach(c => {
              if (c.state === 'at_risk' || (c.state === 'due' && c.due_at && (new Date(c.due_at).getTime() - nowMs) <= 24 * HOUR))
                prevent.push({ ...c, patient_name: p.patient_name });
            }));
            if (!prevent.length) return null;
            const groups = {};
            prevent.forEach(c => {
              const key = /round/i.test(c.name) ? 'Rounds' : /treatment plan|tp /i.test(c.name) ? 'Treatment Plans'
                : /note|documentation/i.test(c.name) ? 'Notes' : /vital/i.test(c.name) ? 'Vitals' : 'Other';
              (groups[key] = groups[key] || []).push(c);
            });
            return (
              <div style={{ ...card, padding:'13px 18px', marginBottom:14, display:'flex', gap:26, alignItems:'center', flexWrap:'wrap',
                borderLeft:'4px solid #0878E8' }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:800 }}>Protect Compliance</div>
                  <div style={{ fontSize:11.5, color:T.inkSoft }}>Requirements we can still prevent</div>
                </div>
                {Object.entries(groups).map(([g, items]) => (
                  <div key={g} style={{ display:'flex', gap:9, alignItems:'baseline' }}>
                    <span style={{ fontSize:20, fontWeight:800, color:'#0878E8' }}>{items.length}</span>
                    <div>
                      <div style={{ fontSize:12.5, fontWeight:700 }}>{g}</div>
                      <div style={{ fontSize:11, color:T.inkSoft }}>{items[0].patient_name}{items.length > 1 ? ` +${items.length - 1}` : ''}</div>
                    </div>
                  </div>
                ))}
                <span style={{ flex:1 }} />
                <button style={{ ...inp, cursor:'pointer', fontWeight:700, color:'#0878E8', borderColor:'#0878E8' }}
                  onClick={() => setFilter('at_risk')}>View at-risk</button>
              </div>
            );
          })()}

          <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
            <StatCard n={s.patients_at_risk || 0} text="Patients at Risk" sub="Require attention" tone="red"
              active={filter === 'at_risk'} onClick={() => toggleFilter('at_risk')} />
            <StatCard n={s.safety_critical_open || 0} text="Safety Critical" sub="Immediate action" tone="red"
              active={filter === 'safety_critical'} onClick={() => toggleFilter('safety_critical')} />
            <StatCard n={s.due_today || 0} text="Due Today" sub="Across all patients" tone="amber"
              active={filter === 'due_today'} onClick={() => toggleFilter('due_today')} />
            <StatCard n={s.total_overdue || 0} text="Overdue" sub="Across all patients" tone="red"
              active={filter === 'overdue'} onClick={() => toggleFilter('overdue')} />
            <StatCard n={s.patients || 0} text="Active Patients" tone="ink" />
            <StatCard n={s.license_red || 0} text="License Alerts" sub={s.license_red ? 'Action needed' : 'No active issues'} tone="red" />
            <StatCard n={missSum ? missSum.last_24h : 0} text="Misses (24h)"
              sub={missSum ? (missSum.last_7d + ' in 7 days' + (missSum.unack ? ' \u00b7 ' + missSum.unack + ' unacknowledged' : '')) : 'Missed or done late'}
              tone={missSum && missSum.last_24h ? 'amber' : 'ink'} active={tab === 'facility'} onClick={() => setTab('facility')} />
          </div>

          <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:12, flexWrap:'wrap' }}>
            <input style={{ ...inp, minWidth:180 }} placeholder="Search patients..." value={q} onChange={e => setQ(e.target.value)} />
            <select style={inp} value={locF} onChange={e => setLocF(e.target.value)}>
              <option value="">All LOC</option>
              {locOptions.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <select style={inp} value={sevF} onChange={e => setSevF(e.target.value)}>
              <option value="">All Severity</option>
              <option value="safety_critical">Safety Critical</option>
              <option value="critical">Critical</option>
              <option value="important">Important</option>
            </select>
            {!isSupervisor && myPool && (
              <label style={{ fontSize:13, color:T.inkSoft, display:'flex', alignItems:'center', gap:6 }}>
                <input type="checkbox" checked={mine} onChange={e => setMine(e.target.checked)} />
                Show only my items
              </label>
            )}
            {(filter !== 'all' || q || locF || sevF || mine) &&
              <button style={{ ...inp, cursor:'pointer', fontWeight:700, color:'#0878E8' }}
                onClick={() => { setFilter('all'); setQ(''); setLocF(''); setSevF(''); setMine(false); }}>Clear all</button>}
            <span style={{ flex:1 }} />
            <label style={{ fontSize:13, color:T.inkSoft, display:'flex', alignItems:'center', gap:6 }}>
              <input type="checkbox" checked={showOk} onChange={e => setShowOk(e.target.checked)} />
              Show fully compliant charts
            </label>
          </div>

          {shown.length > 0 && (
            <div style={{ display:'grid', gridTemplateColumns:'minmax(210px,2fr) minmax(110px,1fr) minmax(150px,1.4fr) 130px 70px 80px 90px 28px',
              gap:10, padding:'8px 16px 6px', alignItems:'center' }}>
              <span style={colHead}>Patient</span><span style={colHead}>LOC / Episode</span>
              <span style={colHead}>Status Summary</span><span style={colHead}>Highest Severity</span>
              <span style={{ ...colHead, textAlign:'center' }}>Overdue</span><span style={{ ...colHead, textAlign:'center' }}>Due Soon</span>
              <span style={colHead}>Owners</span><span />
            </div>
          )}

          {data.evaluated === false ? (
            <div style={{ ...card, padding:20, color:T.inkSoft, fontWeight:600, fontSize:14 }}>
              Nothing evaluated. {data.scope_note || 'No facilities are in scope for this account.'}
            </div>
          ) : (data.patients || []).length === 0 ? (
            <div style={{ ...card, padding:20, color:T.inkSoft, fontWeight:600, fontSize:14 }}>
              No patients in scope for this facility - nothing was evaluated.
            </div>
          ) : shown.length === 0 ? (
            <div style={{ ...card, padding:20, color: filter === 'all' && !q && !locF && !sevF ? T.pass : T.inkSoft, fontWeight:600, fontSize:14 }}>
              {filter === 'all' && !q && !locF && !sevF
                ? `All ${(data.patients || []).length} charts evaluated - every chart is current.`
                : 'No charts match this filter.'}
            </div>
          ) : shown.map(p => {
            const hs = highestSev(p);
            const owners = [...new Set(p.checks.filter(c => c.state !== 'satisfied').map(c => c.role_owner).filter(Boolean))];
            const isOpen = open[p.episode_id];
            const visChecks = p.checks.filter(checkVisible);
            const excChecks = visChecks.filter(c => c.state !== 'satisfied');
            const listChecks = expandAll[p.episode_id] ? visChecks : (excChecks.length ? excChecks.concat(visChecks.filter(c => c.state === 'satisfied').slice(0,1)) : visChecks);
            return (
              <div key={p.episode_id} style={{ ...card, marginBottom:10, overflow:'hidden' }}>
                <div onClick={() => setOpen(o => ({ ...o, [p.episode_id]: !o[p.episode_id] }))}
                  style={{ display:'grid', gridTemplateColumns:'minmax(210px,2fr) minmax(110px,1fr) minmax(150px,1.4fr) 130px 70px 80px 90px 28px',
                    gap:10, padding:'11px 16px', alignItems:'center', cursor:'pointer' }}>
                  <div style={{ display:'flex', gap:11, alignItems:'center', minWidth:0 }}>
                    <PatientAvatar patient={p} size={42} radius={9} />
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:14, fontWeight:700, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.patient_name}</div>
                      <div style={{ fontSize:11.5, color:T.inkSoft }}>{p.mrn ? `MRN ${p.mrn}` : ''}</div>
                      <div style={{ fontSize:11.5, color:T.inkSoft }}>Admitted {fmtD(p.admit_date)}{p.discharged ? ' - DISCHARGED' : ''}</div>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize:13.5, fontWeight:700 }}>{p.loc || '-'}</div>
                  </div>
                  <div style={{ display:'grid', gap:3 }}>
                    {(() => {
                      const sc = excChecks.filter(c => c.severity === 'safety_critical' && c.state === 'overdue').length;
                      const od = excChecks.filter(c => c.state === 'overdue').length - sc;
                      const ds = excChecks.filter(c => ['due','at_risk'].includes(c.state)).length;
                      const rows = [];
                      if (sc) rows.push([dot('#b30000'), `${sc} Safety Critical`]);
                      if (od) rows.push([dot('#e05a4e'), `${od} Overdue`]);
                      if (ds) rows.push([dot('#e8a13c'), `${ds} Due Soon`]);
                      if (!rows.length) rows.push([dot(T.pass), 'Current']);
                      return rows.map(([d, t], i) => (
                        <span key={i} style={{ display:'inline-flex', gap:7, alignItems:'center', fontSize:12.5, color:T.ink }}>{d}{t}</span>
                      ));
                    })()}
                  </div>
                  <div>{hs ? SEV_META[hs].chip() : pill(T.passBg, T.pass, 'Current')}</div>
                  <div style={{ textAlign:'center', fontSize:16, fontWeight:800, color: p.overdue_count ? '#b30000' : T.inkSoft }}>{p.overdue_count || 0}</div>
                  <div style={{ textAlign:'center', fontSize:16, fontWeight:800, color: dueSoonCount(p) ? '#8d6e00' : T.inkSoft }}>{dueSoonCount(p)}</div>
                  <div style={{ display:'flex', gap:3 }}>
                    {owners.slice(0,3).map(ro => <span key={ro}>{ownerChip(ro, true)}</span>)}
                    {owners.length > 3 && <span style={{ fontSize:11, color:T.inkSoft, alignSelf:'center' }}>+{owners.length - 3}</span>}
                  </div>
                  <span style={{ color:T.inkSoft, fontSize:13, textAlign:'right' }}>{isOpen ? '\u25be' : '\u25b8'}</span>
                </div>
                {isOpen && (
                  <div style={{ borderTop:`1px solid ${T.b100}`, background:'#fbfcfe' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'minmax(230px,2.2fr) 90px 150px 130px minmax(170px,1.4fr) 120px',
                      gap:10, padding:'9px 16px 4px', alignItems:'center' }}>
                      <span style={colHead}>Requirement</span><span style={colHead}>Category</span>
                      <span style={colHead}>State</span><span style={colHead}>Owner</span>
                      <span style={colHead}>Due / Overdue</span><span style={colHead}>Actions</span>
                    </div>
                    {listChecks.map((c, i) => (
                      <div key={i} style={{ display:'grid', gridTemplateColumns:'minmax(230px,2.2fr) 90px 150px 130px minmax(170px,1.4fr) 120px',
                        gap:10, padding:'9px 16px', alignItems:'start', borderTop:`1px solid ${T.b100}` }}>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:T.ink }}>{c.name}</div>
                          <div style={{ fontSize:11, color:T.inkSoft, fontStyle:'italic', marginTop:2 }}>{c.citation}</div>
                        </div>
                        <div style={{ fontSize:12.5, color:T.inkSoft, paddingTop:2 }}>{CAT_FOR(c)}</div>
                        <div style={{ display:'grid', gap:4, justifyItems:'start' }}>
                          {statePill(c.state)}
                          {c.state !== 'satisfied' && SEV_META[c.severity] && SEV_META[c.severity].chip()}
                        </div>
                        <div style={{ paddingTop:2 }}>{ownerChip(c.role_owner)}</div>
                        <div style={{ fontSize:12, color:T.inkSoft, paddingTop:2 }}>
                          {c.state === 'overdue' && c.hours_overdue != null &&
                            <span style={{ color:'#b30000', fontWeight:700 }}>Overdue {c.hours_overdue >= 48 ? Math.round(c.hours_overdue / 24) + ' days' : c.hours_overdue + ' hrs'}<br /></span>}
                          {c.state === 'due' && c.hours_remaining != null &&
                            <span style={{ color:'#8d6e00', fontWeight:700 }}>{c.hours_remaining}h remaining<br /></span>}
                          {c.due_at ? `Was due ${fmtDT(c.due_at)}` : (c.detail || '')}
                          {c.due_at && c.detail && !/gap/i.test(c.detail) ? '' : (c.due_at && c.detail ? <><br />{c.detail}</> : '')}
                        </div>
                        <div>
                          {c.state !== 'satisfied' && onOpenPatient && (() => {
                            const [act, tabKey] = ACTION_FOR(c);
                            return <button style={{ font:'inherit', fontSize:12, fontWeight:700, padding:'7px 13px',
                              borderRadius:8, border:'1px solid #0878E8', color:'#0878E8', background:'#fff', cursor:'pointer', whiteSpace:'nowrap' }}
                              onClick={(e) => { e.stopPropagation(); onOpenPatient(p.patient_id, tabKey); }}>{act}</button>;
                          })()}
                        </div>
                      </div>
                    ))}
                    {visChecks.length > listChecks.length && (
                      <div style={{ padding:'9px 16px', borderTop:`1px solid ${T.b100}`, textAlign:'center' }}>
                        <button style={{ font:'inherit', fontSize:12.5, fontWeight:700, color:'#0878E8', background:'none', border:'none', cursor:'pointer' }}
                          onClick={() => setExpandAll(x => ({ ...x, [p.episode_id]: true }))}>
                          View all {visChecks.length} requirements {'\u2192'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'staff' && (
        <div>
          {(data.clinician_rollup || []).length > 0 ? (
            <div style={{ ...card, padding:14, marginBottom:14, background:T.warningBg }}>
              <div style={{ ...colHead, marginBottom:8 }}>Late Session Documentation - By Clinician</div>
              {data.clinician_rollup.map((c, i) => (
                <div key={i} style={{ display:'flex', gap:10, alignItems:'center', padding:'4px 0', fontSize:13 }}>
                  <strong style={{ minWidth:160 }}>{c.name}</strong>
                  <span style={{ color:T.warning, fontWeight:600 }}>{c.count} note(s) past 24h</span>
                  <span style={{ color:T.inkSoft }}>oldest {c.oldest_hours}h over</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ ...card, padding:20, color:T.inkSoft, fontSize:13.5 }}>No late documentation by clinician.</div>
          )}
          <div style={{ ...card, padding:16, color:T.inkSoft, fontSize:13 }}>
            Full staff compliance view (items by owner, credential clocks) - coming next build.
          </div>
        </div>
      )}

      {tab === 'tasks' && <ComplianceTasks facilityId={facilityId} />}
      {tab === 'cqi' && <CQI facilityId={facilityId} />}
      {tab === 'facility' && (
        <div>
          {(data.license || []).length > 0 ? (
            <div style={{ ...card, padding:14, marginBottom:14 }}>
              <div style={{ ...colHead, marginBottom:8 }}>License &amp; Certification Clocks</div>
              {data.license.map((h, i) => (
                <div key={i} style={{ display:'flex', gap:10, alignItems:'center', padding:'6px 0',
                  borderTop: i ? `1px solid ${T.b100}` : 'none', fontSize:13 }}>
                  {(LIC_PILL[h.state] || LIC_PILL.ok)()}
                  <strong>{h.name}</strong>
                  <span style={{ color:T.inkSoft }}>#{h.license_number || '-'} - expires {fmtD(h.license_expires)} - {h.days_left} days</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ ...card, padding:20, color:T.inkSoft, fontSize:13.5 }}>No licensed structures with expiration clocks at this facility.</div>
          )}
          <div style={{ marginBottom:14 }}><MissesPanel facilityId={facilityId} /></div>
          <div style={{ ...card, padding:16, color:T.inkSoft, fontSize:13 }}>
            Full facility compliance view (rule inventory, tier filters, surveyor mode) - coming next build.
          </div>
        </div>
      )}
    </div>
  );
}
